import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Sparkles, Save, Plus, X, Download, RefreshCw, Power,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CarnivalSnackAdmin({ token, onBack }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [cfg, setCfg] = useState(null);
  const [allOrders, setAllOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingByGroup, setDownloadingByGroup] = useState(false);
  const [newFlavor, setNewFlavor] = useState("");

  // Local-edit copies so the user can change fields and hit Save once.
  const [snackName, setSnackName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [flavors, setFlavors] = useState([]);
  const [enabled, setEnabled] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, oRes] = await Promise.all([
        axios.get(`${API}/carnival-snack/config`, { headers }),
        axios.get(`${API}/carnival-snack/admin/all`, { headers }),
      ]);
      setCfg(cRes.data);
      setAllOrders(oRes.data);
      setSnackName(cRes.data.snack_name || "Snow Cone");
      setEventDate(cRes.data.event_date || "2026-07-24");
      setFlavors(cRes.data.flavors || []);
      setEnabled(!!cRes.data.enabled);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveConfig = async (overrides = {}) => {
    setSaving(true);
    try {
      const body = {
        enabled: overrides.enabled !== undefined ? overrides.enabled : enabled,
        snack_name: snackName,
        event_date: eventDate,
        flavors,
      };
      const res = await axios.put(`${API}/carnival-snack/config`, body, { headers });
      setCfg(res.data);
      setEnabled(!!res.data.enabled);
      toast.success("Saved");
      // Refresh orders so the totals + groups reflect any event-date change.
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (v) => {
    setEnabled(v);
    await saveConfig({ enabled: v });
  };

  const addFlavor = () => {
    const v = newFlavor.trim();
    if (!v) return;
    if (flavors.map(f => f.toLowerCase()).includes(v.toLowerCase())) {
      toast.error("Already in the list");
      return;
    }
    setFlavors([...flavors, v]);
    setNewFlavor("");
  };

  const removeFlavor = (idx) => {
    setFlavors(flavors.filter((_, i) => i !== idx));
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/carnival-snack/admin/pdf`, {
        headers, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Carnival_${snackName.replace(/\s+/g, "_")}_Orders_${eventDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF failed");
    } finally {
      setDownloading(false);
    }
  };

  const downloadPdfByGroup = async () => {
    setDownloadingByGroup(true);
    try {
      const res = await axios.get(`${API}/carnival-snack/admin/pdf-by-group`, {
        headers, responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Carnival_Ices_By_Group_${eventDate}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("By-group PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF failed");
    } finally {
      setDownloadingByGroup(false);
    }
  };

  const grandTotal = useMemo(() => allOrders?.totals?.grand_total || 0, [allOrders]);

  if (loading || !cfg) {
    return (
      <div className="p-8 text-center"><RefreshCw className="w-6 h-6 animate-spin inline" /></div>
    );
  }

  return (
    <div className="space-y-4" data-testid="carnival-admin-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="carnival-admin-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 text-pink-700">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Carnival Snack Order
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Per-camper {cfg.snack_name.toLowerCase()} ordering · resets on season switch
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border rounded-full px-3 py-1.5">
            <Power className={`w-4 h-4 ${enabled ? "text-emerald-600" : "text-gray-400"}`} />
            <span className="text-xs font-semibold">{enabled ? "Open" : "Closed"}</span>
            <Switch
              checked={enabled}
              onCheckedChange={toggleEnabled}
              disabled={saving}
              data-testid="carnival-toggle"
            />
          </div>
          <Button
            variant="outline"
            onClick={downloadPdf}
            disabled={downloading || !grandTotal}
            className="border-pink-300 text-pink-700 hover:bg-pink-50"
            data-testid="carnival-pdf-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            {downloading ? "Building…" : "Print PDF"}
          </Button>
          <Button
            variant="outline"
            onClick={downloadPdfByGroup}
            disabled={downloadingByGroup}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="carnival-pdf-by-group-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            {downloadingByGroup ? "Building…" : "Print by group (one page each)"}
          </Button>
        </div>
      </div>

      {/* Config */}
      <Card className="border-pink-200">
        <CardHeader>
          <CardTitle className="text-sm text-pink-700">Snack Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Snack Name</Label>
              <Input
                value={snackName}
                onChange={(e) => setSnackName(e.target.value)}
                placeholder="Snow Cone"
                className="mt-1"
                data-testid="carnival-snack-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Event Date</Label>
              <Input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="mt-1"
                data-testid="carnival-event-date-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Counselor roster is filtered to kids present this day.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">Flavors</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={newFlavor}
                onChange={(e) => setNewFlavor(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFlavor(); } }}
                placeholder="Add a flavor and press Enter"
                data-testid="carnival-new-flavor-input"
              />
              <Button onClick={addFlavor} variant="outline" data-testid="carnival-add-flavor-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {flavors.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">
                Add at least one flavor before opening orders.
              </p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="carnival-flavor-chips">
              {flavors.map((f, i) => (
                <span
                  key={`${f}-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs bg-pink-50 border border-pink-300 text-pink-800 rounded-full px-2.5 py-1"
                  data-testid={`carnival-flavor-chip-${i}`}
                >
                  {f}
                  <button
                    onClick={() => removeFlavor(i)}
                    className="hover:bg-pink-200 rounded-full p-0.5"
                    title="Remove"
                    data-testid={`carnival-flavor-remove-${i}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <Button
            onClick={() => saveConfig()}
            disabled={saving}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="carnival-save-config-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving…" : "Save Setup"}
          </Button>
        </CardContent>
      </Card>

      {/* Live totals */}
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="text-sm text-amber-700">
            Orders Snapshot · {grandTotal} total across {allOrders?.totals?.group_count || 0} group(s)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {grandTotal === 0 ? (
            <p className="text-sm text-gray-500">No orders saved yet.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="carnival-grand-totals">
                {Object.entries(allOrders?.totals?.by_flavor || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([flavor, count]) => (
                    <span
                      key={flavor}
                      className="inline-flex items-center gap-1.5 text-sm bg-white border-2 border-pink-300 rounded-lg px-3 py-1.5"
                    >
                      <span className="font-semibold">{flavor || "(none)"}</span>
                      <span className="font-bold text-pink-600">{count}</span>
                    </span>
                  ))}
              </div>
              <div className="space-y-2" data-testid="carnival-group-rows">
                {(allOrders?.groups || []).map((g) => (
                  <div
                    key={g.group_code}
                    className="border rounded-lg p-3 bg-white"
                    data-testid={`carnival-group-${g.group_code}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="font-semibold text-pink-700 text-sm">
                        {g.group_code} · {g.total} order{g.total !== 1 ? "s" : ""}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(g.flavor_counts).map(([f, n]) => (
                          <span
                            key={f}
                            className="text-[11px] bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5"
                          >
                            {f || "(none)"} · {n}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
                      {g.orders.map((o, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="truncate">{o.camper_name}</span>
                          <span className="font-medium text-gray-800">{o.flavor || "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
